import { foldableGroups } from "../utils/constants.js";
import { applyVariantCompositeTransform, applyMirror, catalogFootprintArea } from "../utils/geometry.js";
import { appendImage } from "../utils/imagePool.js";
import { assetUrl } from "../config.js";

// Tile is 85px: drop the collection prefix ("Oiko 3S" → "3S"). Full name stays on the tooltip.
const shortItemCode = (furniture, collectionName) => {
  const name = furniture?.name || '';
  const prefix = collectionName || '';
  if (prefix && name.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) {
    return name.slice(prefix.length + 1).trim() || name;
  }
  return name;
};

/** The bottom bar's catalog grid: grouped element cards and their thumbnails. */
export class FurnitureGrid {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;

    this._furnitureGridGeneration = 0; // Guards against concurrent updateFurnitureGrid calls
    this._furnitureGridRendered = null; // Fingerprint of the grid currently on screen; null = rebuild
    // Grid groups the user has unfolded; cleared when the collection changes.
    this._expandedGroups = new Set();
  }

  collapseAllGroups() {
    this._expandedGroups.clear();
  }

  async updateFurnitureGrid() {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    const collections = furnitureManager.getFurnitureCollections();
    const furnitureGrid = document.getElementById("furniture-grid");

    if (!furnitureGrid || !collections[this.ui.currentCategory]) return;

    const generation = ++this._furnitureGridGeneration;

    // Get all furniture from the selected collection
    const selectedSet = collections[this.ui.currentCategory];
    const allFurnitureInSet = selectedSet.items || [];

    // Every component type is listed; the snap (findClosestComponentAttachment) decides what may sit where.
    const country = this.app.getLocalizationManager().getCountry();
    const filteredFurniture = allFurnitureInSet.filter((furniture) =>
      furnitureManager.isProductAvailable(furniture.id, country));

    // Same cards as what's on screen: skip a teardown that would re-decode every thumbnail —
    // only the highlight can differ. Set on a completed build, so an aborted one can't match.
    const fingerprint = [
      this.ui.currentCategory,
      [...this._expandedGroups].sort().join(","),
      filteredFurniture.map((f) => f.id).join(","),
    ].join("|");
    if (fingerprint === this._furnitureGridRendered) {
      this._updateFurnitureSelection();
      return;
    }
    this._furnitureGridRendered = null;
    furnitureGrid.innerHTML = "";

    if (filteredFurniture.length === 0) {
      const emptyMessage = document.createElement("div");
      emptyMessage.className = "empty-catalog-message";
      emptyMessage.textContent = this.ui.t('designerEmptyNoComponents', 'No components available');
      furnitureGrid.appendChild(emptyMessage);
      this._furnitureGridRendered = fingerprint;
      return;
    }

    // Group furniture by componentType
    const groupedFurniture = this.groupFurnitureByComponent(filteredFurniture);

    // Sort component types by order
    const sortedComponentTypes = Object.keys(groupedFurniture).sort((a, b) => {
      const orderA = this.ui.componentCategories[a]?.order || 999;
      const orderB = this.ui.componentCategories[b]?.order || 999;
      return orderA - orderB;
    });

    // A `collapsed` group folds behind a chip, so the shared accessories don't outnumber
    // a collection's own modules on the opening view.
    const foldable = foldableGroups(sortedComponentTypes, this.ui.componentCategories);

    // Display furniture grouped by component type, a rule between kinds as between fabric groups
    for (const [index, componentType] of sortedComponentTypes.entries()) {
      const furnitureInGroup = groupedFurniture[componentType];
      const isFoldable = foldable.has(componentType);
      const isExpanded = !isFoldable || this._expandedGroups.has(componentType);

      if (index > 0) {
        const divider = document.createElement("div");
        divider.className = "material-group-divider";
        furnitureGrid.appendChild(divider);
      }

      if (isFoldable) {
        furnitureGrid.appendChild(
          this.createFurnitureGroupChip(componentType, furnitureInGroup, isExpanded)
        );
      }

      if (!isExpanded) continue;

      // Create furniture items in this group
      for (const furniture of furnitureInGroup) {
        // Abort if a newer updateFurnitureGrid call has started
        if (generation !== this._furnitureGridGeneration) return;

        const item = document.createElement("div");
        item.className = "furniture-item";
        item.dataset.furnitureId = furniture.id;

        // Create thumbnail
        const thumbnail = await this.createFurnitureThumbnail(furniture);

        // Re-check after async work
        if (generation !== this._furnitureGridGeneration) return;

        item.appendChild(thumbnail);

        // Short code only — the full name is on the tooltip and in the selection header.
        const label = document.createElement("div");
        label.className = "furniture-label";
        label.textContent = shortItemCode(furniture, collections[furniture.collection]?.name);
        item.title = furniture.name || '';
        item.appendChild(label);

        item.addEventListener("pointerdown", (e) => {
          // Release implicit pointer capture so pointermove events reach
          // document listeners immediately (not coalesced until pointerup)
          e.currentTarget.releasePointerCapture(e.pointerId);
          this.ui.startBarItemDrag(furniture, e);
        });

        furnitureGrid.appendChild(item);
      }
    }

    this._updateFurnitureSelection(furnitureGrid);
    this._furnitureGridRendered = fingerprint;
    this.ui._updateBarScrollButtons();
  }

  _updateFurnitureSelection(furnitureGrid = document.getElementById("furniture-grid")) {
    const selectedId = this.ui.selectedFurnitureType?.id;
    for (const item of furnitureGrid?.querySelectorAll(".furniture-item") || []) {
      item.classList.toggle("selected", item.dataset.furnitureId === selectedId);
    }
  }

  /**
   * The disclosure control for a collapsible grid group. It fans the group's first few
   * baked thumbnails behind the label so it reads as "more items are in here" rather than
   * as a button — a bare label is the one thing that makes a collapsed group easy to miss.
   */
  createFurnitureGroupChip(componentType, furnitureInGroup, isExpanded) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = `furniture-group-chip ${isExpanded ? "expanded" : ""}`;
    chip.dataset.groupType = componentType;
    chip.setAttribute("aria-expanded", String(isExpanded));

    const fan = document.createElement("div");
    fan.className = "group-chip-fan";
    for (const furniture of furnitureInGroup.slice(0, 3)) {
      if (!furniture.urlCode) continue;
      // No live-render fallback: a missing baked shot just leaves one fewer card in the fan.
      appendImage(fan, [assetUrl(`thumbnails/elements/${furniture.urlCode}.webp`)], {
        onExhausted: (img) => img.remove(),
      });
    }
    chip.appendChild(fan);

    const addSpan = (className, text) => {
      const span = document.createElement("span");
      span.className = className;
      span.textContent = text;
      chip.appendChild(span);
      return span;
    };
    // Catalog-authored, like the furniture.name on the cards beside it — not a t() string.
    addSpan("group-chip-label", this.ui.componentCategories[componentType]?.name || componentType);
    addSpan("group-chip-count", furnitureInGroup.length);

    chip.addEventListener("click", () => this.toggleFurnitureGroup(componentType));
    return chip;
  }

  async toggleFurnitureGroup(componentType) {
    const expanding = !this._expandedGroups.has(componentType);
    if (expanding) {
      this._expandedGroups.add(componentType);
    } else {
      this._expandedGroups.delete(componentType);
    }
    await this.updateFurnitureGrid();
    // The chip sits at the right end of the strip, so bring it to the left edge and the
    // group unrolls into view. Only on the way open — folding back yanks the view for nothing.
    if (expanding) {
      this.ui._scrollBarTo(`.furniture-group-chip[data-group-type="${componentType}"]`, "start");
    }
  }

  groupFurnitureByComponent(furnitureList) {
    const grouped = {};

    for (const furniture of furnitureList) {
      const componentType = furniture.componentType || "uncategorized";

      if (!grouped[componentType]) {
        grouped[componentType] = [];
      }

      grouped[componentType].push(furniture);
    }

    // Modules read as a size run — smallest first. Measured from the authored collision
    // footprint, so it holds across collections without a hand-kept order. An item that
    // authors none keeps its catalog position at the end of the run.
    if (grouped.module) {
      const area = new Map(grouped.module.map(f => [f.id, catalogFootprintArea(f)]));
      grouped.module.sort((a, b) => {
        const areaA = area.get(a.id);
        const areaB = area.get(b.id);
        if (areaA == null || areaB == null) return (areaA == null) - (areaB == null);
        return areaA - areaB;
      });
    }

    return grouped;
  }

  async createFurnitureThumbnail(furniture) {
    const thumbnail = document.createElement("div");
    thumbnail.className = "furniture-thumbnail";

    // Try to generate wireframe thumbnail
    const thumbnailRenderer = this.app.getThumbnailRenderer();
    const furnitureManager = this.app.getFurnitureManager();

    if (thumbnailRenderer && furnitureManager) {
      this.generateFurnitureThumbnailAsync(furniture, thumbnail);
    } else {
      // Fallback to emoji icons if thumbnail renderer not available
      this.createFallbackThumbnail(furniture, thumbnail);
    }

    return thumbnail;
  }

  generateFurnitureThumbnailAsync(furniture, thumbnail) {
    // Prefer the pre-baked product shot so browsing the catalog doesn't pull every element's GLTF/BIN; live render is the fallback for urlCodes without a baked webp.
    const urlCode = furniture.urlCode;
    if (urlCode) {
      appendImage(thumbnail, [assetUrl(`thumbnails/elements/${urlCode}.webp`)], {
        alt: furniture.name,
        className: "furniture-thumbnail-image",
        onExhausted: () => this.renderFurnitureThumbnailLive(furniture, thumbnail),
      });
      return;
    }
    this.renderFurnitureThumbnailLive(furniture, thumbnail);
  }

  async renderFurnitureThumbnailLive(furniture, thumbnail) {
    // Only this path waits on a model load, so the placeholder belongs here rather than on
    // every card — the baked shot replaces it synchronously.
    thumbnail.innerHTML = '<div class="furniture-thumbnail-loading">...</div>';

    const thumbnailRenderer = this.app.getThumbnailRenderer();
    const furnitureManager = this.app.getFurnitureManager();
    if (!thumbnailRenderer || !furnitureManager) {
      thumbnail.innerHTML = "";
      this.createFallbackThumbnail(furniture, thumbnail);
      return;
    }

    try {
      const modelEntry = await furnitureManager.getOrLoadModel(furniture.model);
      const modelMesh = modelEntry.mesh;

      // Generate textured thumbnail with ET-14 fabric
      // Use furniture.id (not urlCode) so variants sharing a model get distinct thumbnails
      const cacheKey = `catalog_${furniture.id}`;

      // Clone the source when we need to mutate transforms (mirror flip, or
      // applying variant 0's composite offset on items with embedded composite groups).
      // The cached modelEntry.mesh must remain at its authored rest pose since other
      // call sites clone from it.
      const needsMirror = !!furniture.properties?.mirror;
      const needsCompositeTransform = !!furniture.properties?.compositeGroupName;
      const hasAutoAttach = !!furniture.properties?.attachChildOnSpawn?.length;
      const needsClone = needsMirror || needsCompositeTransform;

      let meshToRender = modelMesh;
      if (hasAutoAttach) {
        // Combos that auto-attach a child on spawn (e.g. R+H) render as the
        // composite. Geometry is shared with the model cache, so it must NOT be
        // disposed afterward (unlike the mirror/composite clone below).
        const composite = await furnitureManager.buildAutoAttachCompositeMesh(furniture);
        if (composite) meshToRender = composite;
      } else if (needsClone) {
        meshToRender = modelMesh.clone(true);
        if (needsMirror) {
          applyMirror(meshToRender, furniture.properties.mirror);
        }
        if (needsCompositeTransform) {
          applyVariantCompositeTransform(meshToRender, furniture, 0);
        }
      }

      const dataUrl = await thumbnailRenderer.generateThumbnailFromMesh(
        meshToRender,
        cacheKey,
        { size: 128 }
      );

      if (needsClone && !hasAutoAttach) {
        meshToRender.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
        });
      }

      if (dataUrl) {
        // Replace placeholder with actual thumbnail
        thumbnail.innerHTML = '';

        const img = document.createElement("img");
        img.src = dataUrl;
        img.alt = furniture.name;
        img.className = "furniture-thumbnail-image";
        img.draggable = false;
        thumbnail.appendChild(img);
      } else {
        // Fallback if thumbnail generation failed
        thumbnail.innerHTML = '';
        this.createFallbackThumbnail(furniture, thumbnail);
      }
    } catch (error) {
      console.warn(`Failed to generate thumbnail for ${furniture.name}:`, error);
      // Fallback on error
      thumbnail.innerHTML = '';
      this.createFallbackThumbnail(furniture, thumbnail);
    }
  }

  createFallbackThumbnail(furniture, thumbnail) {
    const iconContainer = document.createElement("div");
    iconContainer.className = "furniture-thumbnail-icon";

    const icons = {
      sofa: "S",
      chair: "C",
      table: "T",
      storage: "St",
      ottoman: "O",
    };

    iconContainer.textContent = icons[furniture.type] || "F";

    const nameLabel = document.createElement("div");
    nameLabel.className = "furniture-thumbnail-label";
    nameLabel.textContent = furniture.name.replace(/^.*?\s/, "");

    const pattern = document.createElement("div");
    pattern.className = "furniture-thumbnail-pattern";

    thumbnail.appendChild(pattern);
    thumbnail.appendChild(iconContainer);
    thumbnail.appendChild(nameLabel);
  }
}
